import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import {
  type CountryCode,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { type IDropdownItem, showToast } from 'shared/ui'
import { CountryFlag } from 'shared/ui/CountryFlag/CountryFlag'
import { isCountryCode, isEmail, logger, submitDataToHubspotForm } from 'shared/lib'
import { MAX_FIRST_NAME_LENGTH, MAX_LAST_NAME_LENGTH } from 'shared/constants/auth'
import { PhoneInputStore } from 'shared/ui/PhoneWithLabelInput'
import { COMPANY_SIZES, CompanySizeEnum } from 'shared/constants/companySize'
import { CONTACTS_COUNTS } from 'shared/constants/contactsCounts'
import { getEnvVar } from 'shared/config/lib/getEnvVar.ts'
import { CRMS, CrmEnum, type ICrm } from 'entities/Integrations'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { GetDemoModalContent } from 'entities/Subscription/ui/GetDemoModal/ui/GetDemoModalContent'
import { GetDemoModalActions } from 'entities/Subscription/ui/GetDemoModal/ui/GetDemoModalActions'
import { GetDemoConfirmationContent } from 'entities/Subscription/ui/GetDemoModal/ui/GetDemoConfirmationContent'
import { AuthApi } from 'entities/Auth/api'
import { type IParamsSignUpAnswers } from 'entities/Auth/store/types'
import { OrganizationApi } from 'entities/Organization'

type IGetDemoFormErrors = {
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  contactsRange?: string
  company?: string
  companySize?: string
  crm?: string
  consent?: string
}

let activeModalId: string | null = null

export class GetDemoModalStore {
  private _firstName = ''
  private _lastName = ''
  private _email = ''
  private _contactsRange: string | null = null
  private _company: string | null = null
  private _companySize: string | null = null
  private _crm: ICrm | null = null
  private _consent = false
  private _loading = false
  private _submitted = false
  private _closed = false
  private _errors: IGetDemoFormErrors = {}
  private readonly _phoneInputStore: PhoneInputStore
  private readonly _modalId = nanoid()

  constructor(private _entities: IEntitiesStore) {
    this._phoneInputStore = new PhoneInputStore({
      validate: (number, country) => {
        if (!number.trim()) return false
        if (!isCountryCode(country.id)) return false
        return isValidPhoneNumber(number, country.id)
      },
      invalidNumberMessage: 'Enter a valid phone number',
    })

    makeAutoObservable(this)
  }

  open = () => {
    if (activeModalId) return
    activeModalId = this._modalId
    this._open()
    this.init()
  }

  init = async () => {
    let answers: IParamsSignUpAnswers | null = null

    try {
      const response = await OrganizationApi.getOrganizationAnswers()
      answers = response.data.data?.items?.answers ?? null
    } catch (e) {
      logger.error(e)
    }

    if (answers) {
      this._prefillFromRegistrationAnswers(answers)
    }

    await this._loadCountries(answers?.country ?? null)

    this._prefillFromProfile()
    this._prefillPhone(answers)
  }

  private _loadCountries = async (preferredCountry: string | null = null) => {
    try {
      const { data } = await AuthApi.getCountries()
      const items: IDropdownItem[] = data
        .filter((country): country is { key: CountryCode; label: string } =>
          isCountryCode(country.key)
        )
        .map((country): IDropdownItem => {
          const callingCode = getCountryCallingCode(country.key)
          return {
            id: country.key,
            label: country.label,
            searchPattern: `${country.label} +${callingCode} ${callingCode}`,
            customIconL: <CountryFlag country={country.key} height={16} />,
            labelRight: `+${callingCode}`,
            labelRightVariant: 'body-md-regular',
          }
        })
        .sort((a, b) => a.label.localeCompare(b.label))

      if (!items.length) return

      this._phoneInputStore.setCustomCountries(items)

      const country = preferredCountry || this._entities.usersStore.user?.country
      if (country && isCountryCode(country)) {
        this._phoneInputStore.setCountryById(country)
      } else {
        this._phoneInputStore.setCountryById('US')
      }
    } catch (e) {
      logger.error(e)
    }
  }

  get firstName() {
    return this._firstName
  }

  get lastName() {
    return this._lastName
  }

  get email() {
    return this._email
  }

  get contactsRange() {
    return this._contactsRange
  }

  get companySize() {
    return this._companySize
  }

  get company() {
    return this._company
  }

  get crm() {
    return this._crm
  }

  get consent() {
    return this._consent
  }

  get loading() {
    return this._loading
  }

  get phoneInputStore() {
    return this._phoneInputStore
  }

  get errors() {
    return this._errors
  }

  get contactsRangeOptions(): IDropdownItem[] {
    return CONTACTS_COUNTS
  }

  get companySizeOptions(): IDropdownItem[] {
    return COMPANY_SIZES
  }

  setFirstName = (value: string) => {
    if (value.length > MAX_FIRST_NAME_LENGTH) return
    this._firstName = value
    if (this._errors.firstName) this._errors = { ...this._errors, firstName: undefined }
  }

  setLastName = (value: string) => {
    if (value.length > MAX_LAST_NAME_LENGTH) return
    this._lastName = value
    if (this._errors.lastName) this._errors = { ...this._errors, lastName: undefined }
  }

  setEmail = (value: string) => {
    this._email = value.trim()
    if (this._errors.email) this._errors = { ...this._errors, email: undefined }
  }

  setCompany = (value: string) => {
    this._company = value.trim()
    if (this._errors.company) this._errors = { ...this._errors, company: undefined }
  }

  setContactsRange = (value: string) => {
    this._contactsRange = value
    if (this._errors.contactsRange) this._errors = { ...this._errors, contactsRange: undefined }
  }

  setCompanySize = (value: string) => {
    this._companySize = value
    if (this._errors.companySize) this._errors = { ...this._errors, companySize: undefined }
  }

  setCrm = (value: ICrm | null) => {
    this._crm = value
    if (this._errors.crm) this._errors = { ...this._errors, crm: undefined }
  }

  toggleConsent = () => {
    this._consent = !this._consent
    if (this._consent && this._errors.consent) {
      this._errors = { ...this._errors, consent: undefined }
    }
  }

  private _prefillFromProfile = () => {
    const user = this._entities.usersStore.user

    if (user) {
      this._firstName = user.first_name || ''
      this._lastName = user.last_name || ''
      this._email = user.email || ''
    }

    const organizationName = this._entities.organizationStore.name
    if (organizationName) {
      this._company = organizationName
    }
  }

  private _prefillPhone = (answers: IParamsSignUpAnswers | null) => {
    if (answers?.number) {
      this._setPhoneNumber(answers.number, answers.country)
      return
    }

    const user = this._entities.usersStore.user
    if (!user) return

    const country = user.country
    if (user.national_number) {
      this._setPhoneNumber(user.national_number, country)
    } else if (user.number) {
      this._setPhoneNumber(user.number, country)
    }
  }

  private _setPhoneNumber = (rawNumber: string, countryHint: string | null | undefined) => {
    const country: CountryCode | undefined = isCountryCode(countryHint ?? '')
      ? (countryHint as CountryCode)
      : undefined
    const parsed = parsePhoneNumberFromString(rawNumber, country)
    this._phoneInputStore.setNumber(parsed?.nationalNumber ?? rawNumber)
  }

  private _migrateCompanySize = (value: string): string => {
    if (value === '100+') return CompanySizeEnum.size_100_249
    return value
  }

  private _prefillFromRegistrationAnswers = (answers: IParamsSignUpAnswers) => {
    if (answers.first_name) this._firstName = answers.first_name.slice(0, MAX_FIRST_NAME_LENGTH)
    if (answers.last_name) this._lastName = answers.last_name.slice(0, MAX_LAST_NAME_LENGTH)
    if (answers.email) this._email = answers.email.trim()
    if (answers.organization_name) this._company = answers.organization_name.trim()

    if (answers.answer_contactsCount) {
      const isValidContacts = CONTACTS_COUNTS.some(
        (item) => item.id === answers.answer_contactsCount
      )
      if (isValidContacts) this._contactsRange = answers.answer_contactsCount
    }

    if (answers.answer_team_size) {
      const migratedSize = this._migrateCompanySize(answers.answer_team_size)
      const isValidSize = COMPANY_SIZES.some((item) => item.id === migratedSize)
      if (isValidSize) this._companySize = migratedSize
    }

    if (answers.answer_integration) {
      const crm = CRMS.find((item) => item.name === answers.answer_integration)
      if (crm) {
        this._crm = crm
      } else {
        this._crm = {
          id: CrmEnum.other,
          value: 'Other',
          name: answers.answer_integration,
          icon: 'lightning2',
        }
      }
    }
  }

  private _validate = (): boolean => {
    const errors: IGetDemoFormErrors = {}

    if (!this._firstName.trim()) errors.firstName = 'Enter first name'
    if (!this._lastName.trim()) errors.lastName = 'Enter last name'

    const email = this._email.trim()
    if (!email) {
      errors.email = 'Enter work email'
    } else if (!isEmail(email)) {
      errors.email = 'Enter a valid email'
    }

    if (!this._phoneInputStore.number.trim()) {
      errors.phone = 'Enter phone number'
    } else if (!this._phoneInputStore.isValidNumber) {
      errors.phone = 'Enter a valid phone number'
    }

    if (!this._company?.trim()) errors.company = 'Enter company name'
    if (!this._contactsRange) errors.contactsRange = 'Select how many contacts do you have'
    if (!this._companySize) errors.companySize = 'Select your company size'
    if (!this._crm) errors.crm = 'Select your CRM'
    if (!this._consent) errors.consent = 'Agree to receive text messages to continue'

    this._errors = errors

    return Object.keys(errors).length === 0
  }

  submit = async () => {
    if (this._loading) return
    if (!this._validate()) return

    try {
      runInAction(() => {
        this._loading = true
      })

      const fields = [
        {
          name: 'firstname',
          value: this._firstName.trim(),
        },
        {
          name: 'lastname',
          value: this._lastName.trim(),
        },
        {
          name: 'email',
          value: this._email.trim(),
        },
        {
          name: 'phone',
          value: this._phoneInputStore.fullUnformattedNumber,
        },
        {
          name: 'contacts',
          value: this._contactsRange ?? '',
        },
        {
          name: 'company',
          value: this._company ?? '',
        },
        {
          name: 'team_size',
          value: this._companySize ?? '',
        },
        {
          name: 'integration',
          value: this._crm?.name ?? '',
        },
        {
          name: 'salesmsg_messaging_consent',
          value: this._consent.toString(),
        },
      ]

      await submitDataToHubspotForm({
        portalId: getEnvVar('HUBSPOT_PORTAL_ID_GET_DEMO'),
        formId: getEnvVar('HUBSPOT_FORM_ID_GET_DEMO'),
        fields: fields,
        successCallback: () => {
          if (this._closed) return

          runInAction(() => {
            this._submitted = true
          })

          this._openConfirmation()
        },
        isShowErrorMessage: true,
      })
    } catch (error) {
      logger.error(error)

      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again',
      })
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  private _open = () => {
    modalStore.addModal({
      id: this._modalId,
      showHeader: false,
      showCloseButton: false,
      showCloseIcon: false,
      width: 480,
      hiddenContent: true,
      ModalContent: () => <GetDemoModalContent store={this} />,
      ModalActions: () => <GetDemoModalActions store={this} />,
      onClose: this.close,
    })
  }

  private _openConfirmation = () => {
    modalStore.removeModal(this._modalId)
    this._releaseActive()

    const confirmationId = `${this._modalId}-success`

    modalStore.addModal({
      id: confirmationId,
      width: 480,
      showHeader: false,
      showCloseButton: false,
      showCloseIcon: false,
      ModalContent: () => <GetDemoConfirmationContent />,
      primaryAction: {
        text: 'Done',
        fullWidth: true,
        onAction: () => modalStore.closeModal(confirmationId),
      },
      onClose: () => {
        modalStore.removeModal(confirmationId)
      },
    })
  }

  close = () => {
    if (this._closed) return
    this._closed = true

    if (!this._submitted) {
      modalStore.removeModal(this._modalId)
    }
    this._releaseActive()
  }

  private _releaseActive = () => {
    if (activeModalId === this._modalId) activeModalId = null
  }
}
